import type { CreateItem, Item, ListResponse } from '@streepaf/shared'
import { generateKeyBetween } from 'fractional-indexing'
import { ApiError } from '../api/client.js'
import { listsApi } from '../api/lists.js'
import { i18n, t } from '../i18n/index.svelte.js'
import { type ConnectionState, ListStream } from '../realtime/sse.js'
import { clientId, itemId as newItemId } from '../utils/ids.js'
import { myLists } from './myLists.svelte.js'
import { PendingQueue } from './pendingQueue.svelte.js'
import { toasts } from './toasts.svelte.js'

export interface SectionSummary {
  id: string
  text: string
  total: number
  checked: number
}

type Status = 'loading' | 'ready' | 'missing' | 'failed'

function bySection(items: Item[]): SectionSummary[] {
  const out: SectionSummary[] = []
  let current: SectionSummary | null = null
  for (const item of items) {
    if (item.kind === 'section') {
      current = { id: item.id, text: item.text, total: 0, checked: 0 }
      out.push(current)
      continue
    }
    if (!current) continue
    current.total++
    if (item.checked) current.checked++
  }
  return out
}

/**
 * Owns one list: optimistic edits, the live stream, and the reconciliation
 * between them. Local changes always win over an incoming event for a field
 * that is still waiting to be sent, so a checkbox never flips back under you.
 */
export class ListStore {
  status = $state<Status>('loading')
  list = $state<ListResponse | null>(null)
  items = $state<Item[]>([])
  connection = $state<ConnectionState>('connecting')
  editingId = $state<string | null>(null)
  remoteChanged = $state<Record<string, number>>({})

  readonly queue = new PendingQueue((error) => {
    toasts.error(
      error.isRateLimited
        ? t('toast_rate_limited')
        : t('toast_save_failed', { reason: error.message }),
    )
  })

  private stream: ListStream | null = null
  private pendingFields = new Map<string, Set<string>>()
  private token = ''

  get capability(): 'view' | 'edit' | 'admin' {
    return this.list?.capability ?? 'view'
  }

  get canEdit(): boolean {
    return this.capability !== 'view'
  }

  get unchecked(): Item[] {
    return this.items.filter((item) => item.kind === 'section' || !item.checked)
  }

  get completed(): Item[] {
    return this.items
      .filter((item) => item.kind === 'item' && item.checked)
      .sort((a, b) => (b.checkedAt ?? '').localeCompare(a.checkedAt ?? ''))
  }

  get stats(): { total: number; checked: number } {
    const plain = this.items.filter((item) => item.kind === 'item')
    return { total: plain.length, checked: plain.filter((item) => item.checked).length }
  }

  get sections(): SectionSummary[] {
    return bySection(this.items)
  }

  get saving(): boolean {
    return this.queue.count > 0
  }

  async open(token: string): Promise<void> {
    this.token = token
    this.status = 'loading'
    try {
      const list = await listsApi.get(token)
      this.receive(list)
      this.status = 'ready'
      this.remember()
      this.connect()
    } catch (error) {
      if (error instanceof ApiError && error.isMissing) {
        this.status = 'missing'
        myLists.markMissing(token)
        return
      }
      this.status = 'failed'
    }
  }

  close(): void {
    this.stream?.stop()
    this.stream = null
  }

  private connect(): void {
    this.stream?.stop()
    this.stream = new ListStream(this.token, {
      onEvent: (name, data) => this.applyEvent(name, data as Record<string, unknown>),
      onState: (state) => {
        this.connection = state
        if (state === 'live') this.queue.flush()
      },
      onResync: () => void this.resync(),
    })
    this.stream.start()
  }

  private async resync(): Promise<void> {
    try {
      const list = await listsApi.get(this.token)
      this.receive(list)
    } catch (error) {
      if (error instanceof ApiError && error.isMissing) this.status = 'missing'
    }
  }

  private receive(list: ListResponse): void {
    this.list = list
    // Anything still queued locally is newer than this snapshot.
    this.items = list.items.map((incoming) => this.preserveLocal(incoming))
    this.remember()
  }

  private preserveLocal(incoming: Item): Item {
    const pending = this.pendingFields.get(incoming.id)
    if (!pending) return incoming
    const local = this.items.find((item) => item.id === incoming.id)
    if (!local) return incoming
    const merged = { ...incoming }
    for (const field of pending) {
      ;(merged as unknown as Record<string, unknown>)[field] = (
        local as unknown as Record<string, unknown>
      )[field]
    }
    return merged
  }

  private remember(): void {
    if (!this.list) return
    myLists.remember({
      token: this.token,
      role: this.list.capability,
      title: this.list.title,
      emoji: this.list.emoji,
      total: this.stats.total,
      checked: this.stats.checked,
    })
  }

  applyEvent(name: string, data: Record<string, unknown>): void {
    const origin = data.origin as string | null
    if (origin === clientId()) {
      if (typeof data.version === 'number' && this.list) this.list.version = data.version
      return
    }

    switch (name) {
      case 'snapshot':
        this.receive(data.list as ListResponse)
        break
      case 'list.updated': {
        const patch = data.list as Partial<ListResponse>
        if (this.list) this.list = { ...this.list, ...patch, version: data.version as number }
        break
      }
      case 'item.created': {
        const item = data.item as Item
        if (!this.items.some((existing) => existing.id === item.id)) {
          this.items = sortItems([...this.items, item])
          this.pulse(item.id)
        }
        break
      }
      case 'item.updated': {
        const item = this.preserveLocal(data.item as Item)
        this.items = sortItems(
          this.items.map((existing) => (existing.id === item.id ? item : existing)),
        )
        this.pulse(item.id)
        break
      }
      case 'item.deleted': {
        const ids = new Set(data.ids as string[])
        if (this.editingId && ids.has(this.editingId)) {
          this.editingId = null
          toasts.show(t('toast_item_gone'))
        }
        this.items = this.items.filter((item) => !ids.has(item.id))
        break
      }
      case 'list.deleted':
        this.status = 'missing'
        myLists.forget(this.token)
        break
    }
    if (typeof data.version === 'number' && this.list) this.list.version = data.version
    this.remember()
  }

  private pulse(id: string): void {
    this.remoteChanged = { ...this.remoteChanged, [id]: Date.now() }
  }

  private markPending(id: string, fields: string[]): void {
    const set = this.pendingFields.get(id) ?? new Set<string>()
    for (const field of fields) set.add(field)
    this.pendingFields.set(id, set)
  }

  private clearPending(id: string, fields: string[]): void {
    const set = this.pendingFields.get(id)
    if (!set) return
    for (const field of fields) set.delete(field)
    if (set.size === 0) this.pendingFields.delete(id)
  }

  private patchLocal(id: string, patch: Partial<Item>): void {
    this.items = this.items.map((item) => (item.id === id ? { ...item, ...patch } : item))
  }

  // --- mutations -----------------------------------------------------------

  addItems(
    drafts: CreateItem[],
    anchor: { afterId?: string | null; beforeId?: string } = {},
  ): Item[] {
    if (drafts.length === 0) return []
    const now = new Date().toISOString()
    const [before, after] = this.boundsFor(anchor)
    let position = before

    const created: Item[] = drafts.map((draft) => {
      position = generateKeyBetween(position, after)
      return {
        id: draft.id ?? newItemId(),
        kind: draft.kind ?? 'item',
        text: draft.text,
        note: draft.note ?? '',
        quantity: draft.quantity ?? null,
        checked: draft.checked ?? false,
        checkedAt: draft.checked ? now : null,
        checkedBy: null,
        claimedBy: draft.claimedBy ?? null,
        position,
        createdAt: now,
        updatedAt: now,
      }
    })

    this.items = sortItems([...this.items, ...created])
    const payload = created.map((item) => ({
      id: item.id,
      kind: item.kind,
      text: item.text,
      note: item.note,
      quantity: item.quantity,
      checked: item.checked,
      claimedBy: item.claimedBy,
    }))

    this.queue.push({
      key: `add:${created[0]?.id}`,
      run: () => listsApi.addItems(this.token, { items: payload, ...anchor }),
      revert: () => {
        const ids = new Set(created.map((item) => item.id))
        this.items = this.items.filter((item) => !ids.has(item.id))
      },
    })
    return created
  }

  private boundsFor(anchor: {
    afterId?: string | null
    beforeId?: string
  }): [string | null, string | null] {
    const ordered = sortItems(this.items)
    if (anchor.beforeId !== undefined) {
      const index = ordered.findIndex((item) => item.id === anchor.beforeId)
      if (index !== -1) {
        return [ordered[index - 1]?.position ?? null, ordered[index]?.position ?? null]
      }
    }
    if (anchor.afterId === null) return [null, ordered[0]?.position ?? null]
    if (anchor.afterId !== undefined) {
      const index = ordered.findIndex((item) => item.id === anchor.afterId)
      if (index !== -1) {
        return [ordered[index]?.position ?? null, ordered[index + 1]?.position ?? null]
      }
    }
    return [ordered[ordered.length - 1]?.position ?? null, null]
  }

  setChecked(id: string, checked: boolean, by?: string): void {
    const item = this.items.find((entry) => entry.id === id)
    if (!item || item.kind === 'section') return
    const previous = { checked: item.checked, checkedAt: item.checkedAt, checkedBy: item.checkedBy }

    this.markPending(id, ['checked', 'checkedAt', 'checkedBy'])
    this.patchLocal(id, {
      checked,
      checkedAt: checked ? new Date().toISOString() : null,
      checkedBy: checked ? (by ?? null) : null,
    })

    this.queue.push({
      key: `item:${id}:checked`,
      run: async () => {
        await listsApi.updateItem(this.token, id, {
          checked,
          checkedBy: checked ? (by ?? null) : null,
        })
        this.clearPending(id, ['checked', 'checkedAt', 'checkedBy'])
      },
      revert: () => {
        this.clearPending(id, ['checked', 'checkedAt', 'checkedBy'])
        this.patchLocal(id, previous)
      },
    })
  }

  updateItem(
    id: string,
    patch: Partial<Pick<Item, 'text' | 'note' | 'quantity' | 'claimedBy'>>,
  ): void {
    const item = this.items.find((entry) => entry.id === id)
    if (!item) return
    const fields = Object.keys(patch)
    const previous = Object.fromEntries(
      fields.map((field) => [field, (item as unknown as Record<string, unknown>)[field]]),
    ) as Partial<Item>

    this.markPending(id, fields)
    this.patchLocal(id, patch)

    this.queue.push({
      key: `item:${id}:${fields.join(',')}`,
      run: async () => {
        await listsApi.updateItem(this.token, id, patch)
        this.clearPending(id, fields)
      },
      revert: () => {
        this.clearPending(id, fields)
        this.patchLocal(id, previous)
      },
    })
  }

  deleteItem(id: string, options: { cascade?: boolean; silent?: boolean } = {}): void {
    const ordered = sortItems(this.items)
    const index = ordered.findIndex((item) => item.id === id)
    const item = ordered[index]
    if (!item) return

    const removed = [item]
    if (options.cascade && item.kind === 'section') {
      for (let i = index + 1; i < ordered.length; i++) {
        const next = ordered[i] as Item
        if (next.kind === 'section') break
        removed.push(next)
      }
    }
    const ids = new Set(removed.map((entry) => entry.id))
    this.items = this.items.filter((entry) => !ids.has(entry.id))

    this.queue.push({
      key: `delete:${id}`,
      run: () => listsApi.deleteItem(this.token, id, options.cascade ?? false),
      revert: () => {
        this.items = sortItems([...this.items, ...removed])
      },
    })

    if (!options.silent) {
      toasts.show(t('toast_item_deleted', { text: item.text }), {
        action: { label: t('undo'), run: () => this.restore(removed) },
      })
    }
  }

  /** Undo re-creates the rows with their original ids and positions. */
  private restore(removed: Item[]): void {
    this.items = sortItems([...this.items, ...removed])
    this.queue.push({
      key: `restore:${removed[0]?.id}`,
      run: () =>
        listsApi.addItems(this.token, {
          items: removed.map((item) => ({
            id: item.id,
            kind: item.kind,
            text: item.text,
            note: item.note,
            quantity: item.quantity,
            checked: item.checked,
            claimedBy: item.claimedBy,
          })),
        }),
      revert: () => {
        const ids = new Set(removed.map((item) => item.id))
        this.items = this.items.filter((item) => !ids.has(item.id))
      },
    })
  }

  move(id: string, anchor: { afterId?: string | null; beforeId?: string }): void {
    const item = this.items.find((entry) => entry.id === id)
    if (!item) return
    const previous = item.position
    const [before, after] = this.boundsFor(anchor)
    if (before === previous || after === previous) return

    const position = generateKeyBetween(before, after)
    this.markPending(id, ['position'])
    this.patchLocal(id, { position })
    this.items = sortItems(this.items)

    this.queue.push({
      key: `item:${id}:position`,
      run: async () => {
        await listsApi.moveItem(this.token, id, anchor)
        this.clearPending(id, ['position'])
      },
      revert: () => {
        this.clearPending(id, ['position'])
        this.patchLocal(id, { position: previous })
        this.items = sortItems(this.items)
      },
    })
  }

  /** Used by drag-and-drop, which hands back the full new order. */
  reorder(ids: string[]): void {
    const previous = this.items
    const byId = new Map(this.items.map((item) => [item.id, item]))
    const positions = ids.map((_, index) => `a${String(index).padStart(6, '0')}`)
    this.items = ids
      .map((id, index) => {
        const item = byId.get(id)
        return item ? { ...item, position: positions[index] as string } : null
      })
      .filter((item): item is Item => item !== null)

    this.queue.push({
      key: 'reorder',
      run: () => listsApi.reorder(this.token, ids),
      revert: () => {
        this.items = previous
      },
    })
  }

  sortAlphabetically(): void {
    const ordered = sortItems(this.items)
    const groups: Item[][] = [[]]
    for (const item of ordered) {
      if (item.kind === 'section') groups.push([item], [])
      else (groups[groups.length - 1] as Item[]).push(item)
    }
    const sorted = groups.flatMap((group) =>
      group.length > 1 || group[0]?.kind !== 'section'
        ? [...group].sort((a, b) => a.text.localeCompare(b.text, i18n.locale))
        : group,
    )
    this.reorder(sorted.map((item) => item.id))
  }

  clearCompleted(): void {
    const removed = this.items.filter((item) => item.kind === 'item' && item.checked)
    if (removed.length === 0) return
    const ids = new Set(removed.map((item) => item.id))
    this.items = this.items.filter((item) => !ids.has(item.id))

    this.queue.push({
      key: 'clear-completed',
      run: () => listsApi.clearCompleted(this.token),
      revert: () => {
        this.items = sortItems([...this.items, ...removed])
      },
    })
    toasts.show(t('toast_completed_cleared', { count: removed.length }), {
      action: { label: t('undo'), run: () => this.restore(removed) },
    })
  }

  uncheckAll(): void {
    const previous = this.items
    const changed = this.items.filter((item) => item.checked).length
    if (changed === 0) return
    this.items = this.items.map((item) =>
      item.checked ? { ...item, checked: false, checkedAt: null, checkedBy: null } : item,
    )

    this.queue.push({
      key: 'uncheck-all',
      run: () => listsApi.uncheckAll(this.token),
      revert: () => {
        this.items = previous
      },
    })
    toasts.show(t('toast_unchecked_all', { count: changed }), {
      action: {
        label: t('undo'),
        run: () => {
          const ids = previous.filter((item) => item.checked).map((item) => item.id)
          this.items = previous
          this.queue.push({
            key: 'uncheck-all-undo',
            run: () => listsApi.bulkCheck(this.token, ids, true),
            revert: () => {},
          })
        },
      },
    })
  }

  updateList(patch: { title?: string; description?: string; emoji?: string | null }): void {
    if (!this.list) return
    const previous = {
      title: this.list.title,
      description: this.list.description,
      emoji: this.list.emoji,
    }
    this.list = { ...this.list, ...patch }
    this.remember()

    this.queue.push({
      key: `list:${Object.keys(patch).join(',')}`,
      run: () => listsApi.update(this.token, patch),
      revert: () => {
        if (this.list) this.list = { ...this.list, ...previous }
      },
    })
  }
}

export function sortItems(items: Item[]): Item[] {
  return [...items].sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : 0))
}
